import { captureException } from '@canvas/sentry-core';

import { ShortcutKeyMap } from './shortcuts-keymap.ts';
import { ShortcutPrinter } from './shortcuts-printer.ts';
import {
  type ShortcutFlattenedWithSubcategory,
  type OperatingSystem,
  type Shortcut,
  type ShortcutFlattened,
} from './shortcuts-types.ts';

export { shortcutMessages } from './i18n.ts';

export const getOperatingSystem = (userAgent?: string): OperatingSystem => {
  let ua = userAgent;
  if (!ua && typeof navigator !== 'undefined') {
    ua = navigator?.userAgent ?? '';
  }
  return ua?.includes('Mac') ? 'mac' : 'non-mac';
};

export class ShortcutLoader<T extends string> {
  private readonly os: OperatingSystem;
  private readonly shortcuts: Map<string, Shortcut<T>>;
  private readonly printer: ShortcutPrinter<T>;
  private readonly aliases: Map<string, Shortcut<T>> = new Map();

  constructor(providedShortcuts: Shortcut<T>[], userAgent?: string) {
    this.os = getOperatingSystem(userAgent);
    this.shortcuts = new Map();
    this.printer = new ShortcutPrinter();

    this.loadShortcuts(providedShortcuts);
  }

  public loadShortcuts(providedShortcuts: Shortcut<T>[]) {
    for (const shortcut of providedShortcuts) {
      this.shortcuts.set(shortcut.id, shortcut);
      if (shortcut.aliases) {
        shortcut.aliases.forEach((alias: string) => {
          this.aliases.set(alias, shortcut);
        });
      }
    }
  }

  public addShortcut(shortcuts: Shortcut<string>[]) {
    this.loadShortcuts(shortcuts as Shortcut<T>[]);
  }

  print(id: string) {
    try {
      const shortcut = this.get(id);
      return this.printer.print(this.os, shortcut.sequence);
    } catch (err) {
      console.error(err);
      captureException(err, {
        tags: {
          captureLocation: 'ShortcutLoader-print-catchAll',
        },
      });

      return [];
    }
  }

  printJoined(id: string) {
    return this.os === 'mac' ? this.print(id).join('') : this.print(id).join(' + ');
  }

  printVerbose(id: string) {
    try {
      const shortcut = this.get(id);
      return this.printer.printVerbose(this.os, shortcut.sequence);
    } catch (err) {
      console.error(err);
      captureException(err, {
        tags: {
          captureLocation: 'ShortcutLoader-printVerbose-catchAll',
        },
      });

      return [];
    }
  }

  contains(id: string): boolean {
    return this.shortcuts.has(id) || this.aliases.has(id);
  }

  get(id: string): Shortcut<T> {
    const shortcut = this.shortcuts.get(id) ?? this.aliases.get(id);
    if (!shortcut) {
      throw new Error(`No shortcut found for ${id}...`);
    }
    return shortcut;
  }

  getAll(): Shortcut<T>[] {
    return Array.from(this.shortcuts.values());
  }

  getAllForUI(): {
    shortcuts: ShortcutFlattened[];
    subcategories: Map<string, ShortcutFlattenedWithSubcategory[]>;
  } {
    const shortcuts: ShortcutFlattened[] = [];
    const subcategories = new Map<string, ShortcutFlattenedWithSubcategory[]>();

    for (const [, shortcut] of this.shortcuts) {
      if (!shortcut.hidden) {
        const flattenedShortcut = this.getFlattened(shortcut.id);

        // Regular shortcuts not grouped by subcategory
        if (!flattenedShortcut.subcategory) {
          shortcuts.push(flattenedShortcut);
          continue;
        }

        // Group shortcuts with subcategories defined
        let subcategoryShortcuts = subcategories.get(flattenedShortcut.subcategory.id);
        if (!subcategoryShortcuts) {
          subcategoryShortcuts = [];
          subcategories.set(flattenedShortcut.subcategory.id, subcategoryShortcuts);
        }
        subcategoryShortcuts.push(flattenedShortcut as ShortcutFlattenedWithSubcategory);
      }
    }

    return { shortcuts, subcategories };
  }

  getFlattened(id: string): ShortcutFlattened {
    const shortcut = this.get(id);
    return {
      id: shortcut.id,
      title: shortcut.title,
      type: shortcut.type || 'combination',
      sequence: this.print(shortcut.id),
      subcategory: shortcut.subcategory,
    };
  }

  toKeyMap(): ShortcutKeyMap<T> {
    const keymap = new ShortcutKeyMap<T>(this.os);
    const shortcuts = Array.from(this.shortcuts);
    for (const [, shortcut] of shortcuts) {
      keymap.add(shortcut);
    }

    return keymap;
  }
}
